import React, { useState } from 'react';
import { FunnelLayout } from '../../components/FunnelLayout';
import { FunnelPillButton } from '../../components/funnel/FunnelPillButton';
import { useForm } from '../../context/FormContext';
import { useAppConfig } from '../../context/AppConfigContext';
import { AgeCategory, Gender } from '../../types';

const SENIOR_AGE_THRESHOLD_MONTHS = 84;
// P2 todavía no conoce la raza; el cap "giant" del config (default 24m) es el
// más permisivo de todos. El cross-check fino raza/edad vive en P3.
const CACHORRO_AGE_MAX_MONTHS_DEFAULT = 24;
// Alineado con el backend (schemas/dog.py: ageYears le=25) — un valor mayor
// pasaba P2 y P9 pero reventaba /calculate-plan con 422 permanente en Step11.
const MAX_AGE_YEARS = 25;
const MAX_AGE_MONTHS = 11;

type MicroRange = { min: number; max: number; label: string };

const MICRO_RANGOS: MicroRange[] = [
  { min: 0, max: 6, label: 'cachorro pequeño (0-6m)' },
  { min: 7, max: 12, label: 'cachorro avanzado (7-12m)' },
  { min: 13, max: 36, label: 'adulto joven (1-3a)' },
  { min: 37, max: SENIOR_AGE_THRESHOLD_MONTHS - 1, label: 'adulto estable (4-7a)' },
  { min: SENIOR_AGE_THRESHOLD_MONTHS, max: 120, label: 'senior (7-10a)' },
  { min: 121, max: Number.MAX_SAFE_INTEGER, label: 'senior mayor (10+a)' },
];

function getMicroRango(totalMonths: number): string {
  const safe = Number.isFinite(totalMonths) && totalMonths >= 0 ? totalMonths : 0;
  const found = MICRO_RANGOS.find((r) => safe >= r.min && safe <= r.max);
  return found ? found.label : 'adulto estable (4-7a)';
}

function clampYears(n: number | undefined): number {
  if (typeof n !== 'number' || !Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(MAX_AGE_YEARS, Math.trunc(n)));
}

function clampMonths(n: number | undefined): number {
  if (typeof n !== 'number' || !Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(MAX_AGE_MONTHS, Math.trunc(n)));
}

const CATEGORY_DEFAULTS: Record<AgeCategory, { years: number; months: number }> = {
  [AgeCategory.CACHORRO]: { years: 0, months: 6 },
  [AgeCategory.ADULTO]: { years: 3, months: 0 },
  [AgeCategory.SENIOR]: { years: 8, months: 0 },
};

const AGE_TABS: { key: AgeCategory; label: string; emoji: string; range: string }[] = [
  { key: AgeCategory.CACHORRO, label: 'Cachorro', emoji: '🐶', range: '0-12m' },
  { key: AgeCategory.ADULTO, label: 'Adulto', emoji: '🐕', range: '1-7a' },
  { key: AgeCategory.SENIOR, label: 'Senior', emoji: '🦮', range: '7+a' },
];

interface StepperProps {
  label: string;
  value: number;
  min: number;
  max: number;
  onDecrement: () => void;
  onIncrement: () => void;
  // Si se pasan, los botones siguen activos al llegar al borde y delegan en
  // estos callbacks (rollover meses↔años: 11+ → +1 año, 0− → -1 año).
  onIncrementBeyondMax?: () => void;
  onDecrementBelowMin?: () => void;
}

const Stepper: React.FC<StepperProps> = ({
  label,
  value,
  min,
  max,
  onDecrement,
  onIncrement,
  onIncrementBeyondMax,
  onDecrementBelowMin,
}) => {
  const decDisabled = value <= min && !onDecrementBelowMin;
  const incDisabled = value >= max && !onIncrementBeyondMax;
  const valueId = `stepper-${label}-value`;
  return (
    <div className="flex flex-col items-center gap-2">
      <div className="flex items-center gap-2 sm:gap-3">
        <button
          type="button"
          onClick={() => {
            if (value <= min && onDecrementBelowMin) onDecrementBelowMin();
            else onDecrement();
          }}
          disabled={decDisabled}
          aria-label={`Disminuir ${label}`}
          aria-controls={valueId}
          className="w-10 h-10 sm:w-12 sm:h-12 flex-shrink-0 flex items-center justify-center bg-[#90D1E8] rounded-full text-white font-bold text-xl hover:bg-[#7bc4db] active:scale-95 transition-all disabled:opacity-40 shadow-sm"
        >
          −
        </button>
        <div className="w-12 sm:w-16 flex flex-col items-center">
          <span
            id={valueId}
            role="status"
            aria-live="polite"
            aria-label={`${value} ${label}`}
            className="font-fredoka text-2xl sm:text-3xl font-bold text-[#008BBC] leading-none"
          >
            {value}
          </span>
          <span className="font-poppins text-xs text-black/50 mt-0.5">{label}</span>
        </div>
        <button
          type="button"
          onClick={() => {
            if (value >= max && onIncrementBeyondMax) onIncrementBeyondMax();
            else onIncrement();
          }}
          disabled={incDisabled}
          aria-label={`Aumentar ${label}`}
          aria-controls={valueId}
          className="w-10 h-10 sm:w-12 sm:h-12 flex-shrink-0 flex items-center justify-center bg-[#90D1E8] rounded-full text-white font-bold text-xl hover:bg-[#7bc4db] active:scale-95 transition-all disabled:opacity-40 shadow-sm"
        >
          +
        </button>
      </div>
    </div>
  );
};

const P2DatosBasicos: React.FC = () => {
  const { formData, updateFormData, goToNextStep, goToPreviousStep } = useForm();
  const { t, config } = useAppConfig();

  const nombre = formData.nombre || 'tu perro';
  const [attempted, setAttempted] = useState(false);

  const [ageCategory, setAgeCategory] = useState<AgeCategory | null>(formData.ageCategory ?? null);
  const [ageYears, setAgeYears] = useState<number>(clampYears(formData.ageYears));
  const [ageMonths, setAgeMonths] = useState<number>(clampMonths(formData.ageMonths));

  const [gender, setGender] = useState<Gender | null>(formData.gender ?? null);

  const [isNeutered, setIsNeutered] = useState<boolean | null>(
    typeof formData.isNeutered === 'boolean' ? formData.isNeutered : null,
  );

  const ageTotalMonths = ageYears * 12 + ageMonths;

  const handlePickCategory = (c: AgeCategory) => {
    const defaults = CATEGORY_DEFAULTS[c];
    setAgeCategory(c);
    setAgeYears(defaults.years);
    setAgeMonths(defaults.months);
    updateFormData({
      ageCategory: c,
      ageYears: defaults.years,
      ageMonths: defaults.months,
      ageTotalMonths: defaults.years * 12 + defaults.months,
    });
  };

  const handleAgeYears = (n: number) => {
    const safe = clampYears(n);
    setAgeYears(safe);
    updateFormData({ ageYears: safe, ageTotalMonths: safe * 12 + ageMonths });
  };

  const handleAgeMonths = (n: number) => {
    const safe = clampMonths(n);
    setAgeMonths(safe);
    updateFormData({ ageMonths: safe, ageTotalMonths: ageYears * 12 + safe });
  };

  const handlePickGender = (g: Gender) => {
    setGender(g);
    updateFormData({ gender: g });
  };

  const handlePickNeutered = (b: boolean) => {
    setIsNeutered(b);
    updateFormData({ isNeutered: b });
  };

  const showSeniorWarning = ageCategory === AgeCategory.SENIOR && ageTotalMonths < SENIOR_AGE_THRESHOLD_MONTHS;
  const showAdultSeniorHint = ageCategory === AgeCategory.ADULTO && ageTotalMonths >= SENIOR_AGE_THRESHOLD_MONTHS;

  const cachorroAgeMaxMonths =
    config.nutrition_rules?.cachorro_age_caps_months?.giant ?? CACHORRO_AGE_MAX_MONTHS_DEFAULT;
  const showCachorroWarning = ageCategory === AgeCategory.CACHORRO && ageTotalMonths > cachorroAgeMaxMonths;

  const ageFilled = ageCategory !== null;
  const genderFilled = gender !== null;
  const neuteredFilled = isNeutered !== null;
  const canContinue = ageFilled && genderFilled && neuteredFilled;

  const handleContinue = () => {
    if (!canContinue) return;
    goToNextStep();
  };

  const neuteredTitle = (() => {
    if (gender === Gender.MACHO) {
      return t('p2.neutered_title_macho', '¿Está esterilizado {nombre}?', { nombre });
    }
    if (gender === Gender.HEMBRA) {
      return t('p2.neutered_title_hembra', '¿Está esterilizada {nombre}?', { nombre });
    }
    return t('p2.neutered_title', '¿Está esterilizado/a {nombre}?', { nombre });
  })();

  const sidebarContent = null;

  const missingReason = !ageFilled
    ? t('p2.missing_age', 'Indica la edad para continuar')
    : !genderFilled
      ? t('p2.missing_gender', 'Selecciona el sexo')
      : !neuteredFilled
        ? t('p2.missing_neutered', 'Indica si está esterilizado/a')
        : undefined;

  const ageInvalid = attempted && !ageFilled;
  const genderInvalid = attempted && !genderFilled;
  const neuteredInvalid = attempted && !neuteredFilled;

  return (
    <FunnelLayout
      step={2}
      useTwoPhase
      onContinue={handleContinue}
      continueDisabled={!canContinue}
      continueDisabledReason={missingReason}
      onContinueDisabledAttempt={() => setAttempted(true)}
      onBack={goToPreviousStep}
      sidebar={sidebarContent}
    >
      <div className="flex flex-col gap-5">
        <section
          aria-labelledby="p2-edad-title"
          className={`flex flex-col gap-3 rounded-[18px] transition-all ${
            ageInvalid ? 'ring-2 ring-red-500 ring-offset-2 ring-offset-white p-3' : ''
          }`}
        >
          <h2 id="p2-edad-title" className="font-fredoka text-lg sm:text-xl font-semibold text-center text-black">
            {t('p2.edad_title', '¿Qué edad tiene {nombre}?', { nombre })}
          </h2>

          <div className="grid grid-cols-3 gap-2">
            {AGE_TABS.map((tab) => {
              const isActive = ageCategory === tab.key;
              return (
                <button
                  key={tab.key}
                  type="button"
                  aria-pressed={isActive}
                  onClick={() => handlePickCategory(tab.key)}
                  className={`relative flex flex-col items-center py-4 px-2 rounded-2xl transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[#008BBC]/40
                    ${
                      isActive
                        ? 'bg-[#90D1E8]/10 border-[2.5px] border-[#008BBC] shadow-md'
                        : 'bg-white border-2 border-[#e0e0e0] hover:border-[#90D1E8]'
                    }`}
                >
                  {isActive && (
                    <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-[#008BBC] flex items-center justify-center">
                      <span className="text-white text-[10px] font-bold" aria-hidden="true">
                        ✓
                      </span>
                    </div>
                  )}
                  <span className="text-5xl sm:text-6xl mb-2" aria-hidden="true">
                    {tab.emoji}
                  </span>
                  <span
                    className={`font-fredoka text-sm font-bold leading-tight ${
                      isActive ? 'text-[#008BBC]' : 'text-black/80'
                    }`}
                  >
                    {t(`p2.tab_${tab.key}`, tab.label)}
                  </span>
                  <span
                    className={`font-poppins text-[10px] mt-0.5 ${isActive ? 'text-[#008BBC]/70' : 'text-black/40'}`}
                  >
                    {tab.range}
                  </span>
                </button>
              );
            })}
          </div>

          <div
            className={`flex justify-center gap-4 sm:gap-8 py-2 transition-opacity duration-300 ease-out ${
              ageCategory !== null ? 'opacity-100 pointer-events-auto' : 'opacity-30 pointer-events-none'
            }`}
            aria-hidden={ageCategory === null}
          >
            <Stepper
              label={t('p2.age_years_label', 'años')}
              value={ageYears}
              min={0}
              max={MAX_AGE_YEARS}
              onDecrement={() => handleAgeYears(ageYears - 1)}
              onIncrement={() => handleAgeYears(ageYears + 1)}
            />
            <Stepper
              label={t('p2.age_months_label', 'meses')}
              value={ageMonths}
              min={0}
              max={MAX_AGE_MONTHS}
              onDecrement={() => handleAgeMonths(ageMonths - 1)}
              onIncrement={() => handleAgeMonths(ageMonths + 1)}
              onIncrementBeyondMax={
                ageYears < MAX_AGE_YEARS
                  ? () => {
                      const newYears = ageYears + 1;
                      setAgeYears(newYears);
                      setAgeMonths(0);
                      updateFormData({
                        ageYears: newYears,
                        ageMonths: 0,
                        ageTotalMonths: newYears * 12,
                      });
                    }
                  : undefined
              }
              onDecrementBelowMin={
                ageYears > 0
                  ? () => {
                      const newYears = ageYears - 1;
                      setAgeYears(newYears);
                      setAgeMonths(MAX_AGE_MONTHS);
                      updateFormData({
                        ageYears: newYears,
                        ageMonths: MAX_AGE_MONTHS,
                        ageTotalMonths: newYears * 12 + MAX_AGE_MONTHS,
                      });
                    }
                  : undefined
              }
            />
          </div>

          {showSeniorWarning && (
            <div role="alert" className="bg-amber-50 border border-amber-300 rounded-[14px] p-3">
              <p className="font-poppins text-sm text-amber-800">
                {t('p2.senior_warning', 'Generalmente consideramos senior a partir de los 7 años. ¿Seguro?')}
              </p>
            </div>
          )}

          {showAdultSeniorHint && (
            <div role="alert" className="bg-amber-50 border border-amber-300 rounded-[14px] p-3">
              <p className="font-poppins text-sm text-amber-800">
                {t(
                  'p2.adult_senior_hint',
                  'A partir de los 7 años solemos considerarlo senior. ¿Quieres marcarlo como senior?',
                )}
              </p>
            </div>
          )}

          {showCachorroWarning && (
            <div role="alert" className="bg-amber-50 border border-amber-300 rounded-[14px] p-3">
              <p className="font-poppins text-sm text-amber-800">
                {t(
                  'p2.cachorro_warning',
                  'Con {years}a {months}m casi todas las razas ya son adultas. ¿Seguro que es cachorro?',
                  { years: ageYears, months: ageMonths },
                )}
              </p>
            </div>
          )}

          {ageCategory !== null && (
            <p className="lg:hidden font-poppins text-xs text-[#008BBC] text-center">
              💡{' '}
              {t('p2.micro_rango_compact', 'Ajustamos kcal y suplementos a {rango}', {
                rango: getMicroRango(ageTotalMonths),
              })}
            </p>
          )}
        </section>

        <div className="h-px bg-gray-200" aria-hidden="true" />

        <section
          aria-labelledby="p2-gender-title"
          className={`flex flex-col gap-3 rounded-[18px] transition-all ${
            genderInvalid ? 'ring-2 ring-red-500 ring-offset-2 ring-offset-white p-3' : ''
          }`}
        >
          <h2 id="p2-gender-title" className="font-fredoka text-lg sm:text-xl font-semibold text-center text-black">
            {t('p2.gender_title', '¿Es macho o hembra?')}
          </h2>
          <div className="flex justify-center gap-3">
            <FunnelPillButton
              active={gender === Gender.HEMBRA}
              onClick={() => handlePickGender(Gender.HEMBRA)}
              className="px-5 py-2.5 flex items-center gap-2"
            >
              <span className="text-2xl" aria-hidden="true">
                ♀️
              </span>
              <span>{t('p2.gender_female', 'Hembra')}</span>
            </FunnelPillButton>
            <FunnelPillButton
              active={gender === Gender.MACHO}
              onClick={() => handlePickGender(Gender.MACHO)}
              className="px-5 py-2.5 flex items-center gap-2"
            >
              <span className="text-2xl" aria-hidden="true">
                ♂️
              </span>
              <span>{t('p2.gender_male', 'Macho')}</span>
            </FunnelPillButton>
          </div>
        </section>

        <div className="h-px bg-gray-200" aria-hidden="true" />

        <section
          aria-labelledby="p2-neutered-title"
          className={`flex flex-col gap-3 rounded-[18px] transition-all ${
            neuteredInvalid ? 'ring-2 ring-red-500 ring-offset-2 ring-offset-white p-3' : ''
          }`}
        >
          <h2 id="p2-neutered-title" className="font-fredoka text-lg sm:text-xl font-semibold text-center text-black">
            {neuteredTitle}
          </h2>
          <div className="flex justify-center gap-3 flex-wrap">
            <FunnelPillButton
              active={isNeutered === true}
              onClick={() => handlePickNeutered(true)}
              className="px-5 py-2.5 flex items-center gap-2"
            >
              <span className="text-2xl" aria-hidden="true">
                ✅
              </span>
              <span>{t('p2.neutered_yes', 'Sí')}</span>
            </FunnelPillButton>
            <FunnelPillButton
              active={isNeutered === false}
              onClick={() => handlePickNeutered(false)}
              className="px-5 py-2.5 flex items-center gap-2"
            >
              <span className="text-2xl" aria-hidden="true">
                ❌
              </span>
              <span>{t('p2.neutered_no', 'No')}</span>
            </FunnelPillButton>
          </div>
          {isNeutered !== null && (
            <p className="lg:hidden font-poppins text-xs text-[#008BBC] text-center">
              💡 {t('p2.neutered_hint_compact', 'Ajustamos las kcal a su esterilización')}
            </p>
          )}
        </section>
      </div>
    </FunnelLayout>
  );
};

export default P2DatosBasicos;
